import { z } from "zod";

export const EventStatusSchema = z.enum([
  "booked",
  "menu_confirmed",
  "prepped",
  "delivered",
  "closed",
  "canceled",
]);
export type EventStatus = z.infer<typeof EventStatusSchema>;

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  booked: "Booked",
  menu_confirmed: "Menu confirmed",
  prepped: "Prepped",
  delivered: "Delivered",
  closed: "Closed",
  canceled: "Canceled",
};

export const EVENT_STATUS_BADGE_VARIANT: Record<
  EventStatus,
  "default" | "success" | "warning" | "danger" | "info" | "secondary"
> = {
  booked: "info",
  menu_confirmed: "secondary",
  prepped: "warning",
  delivered: "success",
  closed: "default",
  canceled: "danger",
};

/**
 * The forward-only status walk (booked -> ... -> closed). `canceled` is a
 * separate terminal branch reachable from any non-terminal status, not part
 * of this sequence — the "Cancel event" action is offered independently of
 * the "advance" button (see EventDetailPage).
 */
export const EVENT_STATUS_SEQUENCE: EventStatus[] = [
  "booked",
  "menu_confirmed",
  "prepped",
  "delivered",
  "closed",
];

export function nextEventStatus(current: EventStatus): EventStatus | null {
  const idx = EVENT_STATUS_SEQUENCE.indexOf(current);
  if (idx === -1 || idx === EVENT_STATUS_SEQUENCE.length - 1) return null;
  return EVENT_STATUS_SEQUENCE[idx + 1];
}

export function isTerminalEventStatus(status: EventStatus): boolean {
  return status === "closed" || status === "canceled";
}

export const TaskStatusSchema = z.enum(["open", "done", "waived"]);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  open: "Open",
  done: "Done",
  waived: "Waived",
};

export const EventTaskSchema = z.object({
  id: z.string(),
  event_id: z.string(),
  template_code: z.string(),
  title: z.string(),
  due_date: z.string().nullable(),
  status: TaskStatusSchema,
  done_at: z.string().nullable(),
  notes: z.string().nullable(),
  sort_offset: z.number(),
});
export type EventTask = z.infer<typeof EventTaskSchema>;

// The event-day capture checklist's five non-negotiables (playbook §Proof
// engine). Stored server-side as a JSON object keyed by these strings.
export const CAPTURE_KEYS = [
  "photos_10plus",
  "hero_shot",
  "reveal_clip",
  "host_reaction",
  "na_parity",
] as const;
export const CaptureKeySchema = z.enum(CAPTURE_KEYS);
export type CaptureKey = z.infer<typeof CaptureKeySchema>;

export const CAPTURE_KEY_LABELS: Record<CaptureKey, string> = {
  photos_10plus: "10+ photos",
  hero_shot: "Hero shot",
  reveal_clip: "60s reveal clip",
  host_reaction: "Host reaction",
  na_parity: "NA-parity shot",
};

// Server stores this as a JSON object; not every key is guaranteed present
// until it's been checked at least once, so every value is optional/nullable
// and callers must default a missing key to `false` (see captureValue()).
export const CaptureChecklistSchema = z.record(z.string(), z.boolean().nullable());
export type CaptureChecklist = z.infer<typeof CaptureChecklistSchema>;

export function captureValue(
  checklist: CaptureChecklist | null | undefined,
  key: CaptureKey,
): boolean {
  return !!checklist?.[key];
}

export const EventSchema = z.object({
  id: z.string(),
  quote_id: z.string(),
  contact_id: z.string(),
  event_date: z.string(),
  event_time: z.string().nullable(),
  venue_name: z.string().nullable(),
  venue_address: z.string().nullable(),
  guest_count: z.number(),
  na_guest_count: z.number().nullable(),
  status: EventStatusSchema,
  contract_key: z.string().nullable(),
  coi_sent: z.boolean(),
  deposit_paid_at: z.string().nullable(),
  balance_due_at: z.string().nullable(),
  balance_paid_at: z.string().nullable(),
  capture_checklist: CaptureChecklistSchema.nullable().optional(),
  capture_waived: z.boolean(),
  created_at: z.string(),
  // Present on the detail + calendar endpoints; may be absent on the plain
  // list endpoint (see useEvents.ts / EventsPage.tsx for the resolution).
  tasks: z.array(EventTaskSchema).optional(),
});
export type Event = z.infer<typeof EventSchema>;

export const EventListResponseSchema = z.object({
  items: z.array(EventSchema),
  next_cursor: z.string().nullable(),
});
export type EventListResponse = z.infer<typeof EventListResponseSchema>;

export const CalendarResponseSchema = z.object({
  events: z.array(EventSchema),
});
export type CalendarResponse = z.infer<typeof CalendarResponseSchema>;

// ---- Mutation inputs ----------------------------------------------------

export interface PatchEventInput {
  id: string;
  venue_name?: string | null;
  venue_address?: string | null;
  guest_count?: number;
  na_guest_count?: number | null;
  coi_sent?: boolean;
  event_time?: string | null;
  event_date?: string;
}

export interface RescheduleEventInput {
  id: string;
  event_date: string;
}

export interface PatchEventTaskInput {
  eventId: string;
  taskId: string;
  status?: TaskStatus;
  notes?: string | null;
}

export interface SetEventContractInput {
  id: string;
  contract_key: string;
}

export interface SetEventStatusInput {
  id: string;
  status: EventStatus;
}

export interface SetCaptureInput {
  id: string;
  key: CaptureKey;
  value: boolean;
}

export interface WaiveCaptureInput {
  id: string;
  reason: string;
}
